import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';

const Autocomplete = ({
  value,
  onChange,
  onSelect,
  options = [],
  placeholder = "Escribir para buscar...",
  displayKey = "name",
  valueKey = "id",
  className = "",
  required = false,
  allowCustom = false,
  customLabel = "Crear nuevo"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Update input value when value prop changes
  useEffect(() => {
    if (value && !allowCustom) {
      const selectedOption = options.find(opt => opt[valueKey] === value);
      setInputValue(selectedOption ? selectedOption[displayKey] : '');
    } else if (allowCustom && value) {
      setInputValue(value);
    } else {
      setInputValue('');
    }
  }, [value, options, displayKey, valueKey, allowCustom]);

  // Filter options based on input
  useEffect(() => {
    if (!inputValue.trim()) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option[displayKey].toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [inputValue, options, displayKey]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleOptionSelect = (option) => {
    if (allowCustom) {
      setInputValue(option[displayKey]);
      if (onSelect) {
        onSelect(option[displayKey]);
      }
    } else {
      setInputValue(option[displayKey]);
      if (onSelect) {
        onSelect(option[valueKey]);
      }
    }
    setIsOpen(false);
  };

  const handleCreateCustom = () => {
    if (inputValue.trim() && allowCustom) {
      if (onSelect) {
        onSelect(inputValue.trim());
      }
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length === 1) {
        handleOptionSelect(filteredOptions[0]);
      } else if (allowCustom && inputValue.trim()) {
        handleCreateCustom();
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          className="w-full px-3 py-2 pr-8 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
        />
        <Icon 
          name={isOpen ? "ChevronUp" : "ChevronDown"} 
          size={16} 
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none"
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <button
                key={option[valueKey] || index}
                type="button"
                onClick={() => handleOptionSelect(option)}
                className="w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
              >
                {option[displayKey]}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-slate-500">
              No se encontraron resultados
            </div>
          )}
          
          {allowCustom && inputValue.trim() && !filteredOptions.some(opt => opt[displayKey].toLowerCase() === inputValue.toLowerCase()) && (
            <button
              type="button"
              onClick={handleCreateCustom}
              className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-primary/5 focus:bg-primary/5 focus:outline-none border-t border-slate-200"
            >
              <Icon name="Plus" size={14} className="inline mr-2" />
              {customLabel}: "{inputValue}"
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Autocomplete;

